import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Secao, Vazio, Carregando, Erro, Folha } from '../componentes/ui'
import './regua.css'

/**
 * Como a clínica fala com o paciente.
 *
 * ─── POR QUE É UMA LINHA DO TEMPO, E NÃO UMA TABELA ────────────────
 *
 * A régua é uma sequência de momentos em torno de UM evento: a
 * consulta. Numa tabela, "24" e "-3" são dois números numa coluna
 * chamada "horas" e ninguém lê que um vem antes e o outro depois — foi
 * exatamente assim que o concorrente desenhou, e é por isso que a tela
 * dele exige manual.
 *
 * Aqui a consulta é o marco no meio. O que está à esquerda acontece
 * antes, o que está à direita acontece depois, e a distância até o
 * marco é o `horas_antes`. A regra fica visível sem legenda.
 *
 * ─── A HONESTIDADE QUE ESTA TELA PRECISA TER ───────────────────────
 *
 * Nada é enviado ainda: falta a aprovação do App na Meta. Uma tela que
 * mostra a régua ligada e não avisa disso é uma tela que mente — a
 * clínica configura, espera, e descobre pelo paciente que não chegou.
 * O aviso do canal vem PRIMEIRO, antes de qualquer controle.
 */

type Regua = {
  id: string
  tipo: 'CONFIRMACAO' | 'LEMBRETE' | 'POS_ATENDIMENTO' | 'RECALL' | 'COBRANCA'
  horas_antes: number
  template_id: string | null
  ativo: boolean
  so_primeira: boolean
  min_dias_entre: number
  nao_antes: string
  nao_depois: string
  template_nome: string | null
  template_estado: string | null
  muda: string | null
}

type Canal = { estado: string; telefone_exibido: string | null } | null
type Previsao = { tipo: string; teto_mensagens: string }
type Template = {
  id: string; nome: string; categoria: string; estado: string
  corpo: string; variaveis: number; motivo_rejeicao: string | null
}
type ItemFila = {
  regua_id: string; tipo: string; agendamento_id: string
  paciente: string; inicio: string; previsto_para: string
  janela_aberta: boolean; template_nome: string | null
}

const NOME: Record<Regua['tipo'], string> = {
  CONFIRMACAO:     'Confirmação',
  LEMBRETE:        'Lembrete',
  POS_ATENDIMENTO: 'Depois da consulta',
  RECALL:          'Retorno',
  COBRANCA:        'Cobrança',
}

const EXPLICA: Record<Regua['tipo'], string> = {
  CONFIRMACAO:     'Pergunta se o paciente vem. Sai cedo o bastante para dar tempo de encaixar outra pessoa no lugar.',
  LEMBRETE:        'Só avisa. É o que derruba falta de quem esqueceu, não de quem desistiu.',
  POS_ATENDIMENTO: 'Sai depois do atendimento. Serve para pesquisa, orientação de casa ou retorno.',
  RECALL:          'Chama de volta quem sumiu. É oferta, não aviso — exige que o paciente tenha autorizado.',
  COBRANCA:        'Avisa de pendência. O tom aqui custa mais caro que o texto: mensagem de cobrança errada perde paciente.',
}

/** "3 horas antes" · "1 dia depois" — nunca um número solto. */
function quando(h: number): string {
  const abs = Math.abs(h)
  const lado = h > 0 ? 'antes' : 'depois'
  if (abs === 0) return 'na hora'
  if (abs < 24) return `${abs} ${abs === 1 ? 'hora' : 'horas'} ${lado}`
  const d = Math.round(abs / 24)
  return `${d} ${d === 1 ? 'dia' : 'dias'} ${lado}`
}

const hora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR',
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

export function ReguaWa({ podeEditar }: { podeEditar: boolean }) {
  const [itens, setItens] = useState<Regua[]>([])
  const [canal, setCanal] = useState<Canal>(null)
  const [previsao, setPrevisao] = useState<Previsao[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [editando, setEditando] = useState<Regua | null>(null)
  const [nova, setNova] = useState(false)
  const [fila, setFila] = useState<ItemFila[] | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const [r, t] = await Promise.all([
        api<{ itens: Regua[]; canal: Canal; previsao: Previsao[] }>('/v1/regua'),
        api<{ itens: Template[] }>('/v1/regua/templates'),
      ])
      setItens(r.itens); setCanal(r.canal); setPrevisao(r.previsao)
      setTemplates(t.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar a régua.')
    } finally { setCarregando(false) }
  }, [])

  useEffect(() => { void carregar() }, [carregar])

  async function alternar(r: Regua) {
    try {
      await api(`/v1/regua/${r.id}`, { metodo: 'PATCH', corpo: { ativo: !r.ativo } })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível mudar.')
    }
  }

  async function aplicarPadrao() {
    try {
      await api('/v1/regua/padrao', { metodo: 'POST' })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível aplicar.')
    }
  }

  async function verFila() {
    try {
      const r = await api<{ itens: ItemFila[] }>('/v1/regua/fila')
      setFila(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível ler a fila.')
    }
  }

  const antes = itens.filter((i) => i.horas_antes > 0)
                     .sort((a, b) => b.horas_antes - a.horas_antes)
  const depois = itens.filter((i) => i.horas_antes <= 0)
                      .sort((a, b) => b.horas_antes - a.horas_antes)
  const teto = previsao.reduce((s, p) => s + Number(p.teto_mensagens), 0)

  return (
    <>
      {/* ── o aviso vem antes de tudo ──────────────────────────── */}
      <div className={`rg-canal rg-canal--${canal?.estado === 'ATIVO' ? 'ok' : 'espera'}`}>
        <div>
          <strong>
            {canal?.estado === 'ATIVO'
              ? `WhatsApp conectado${canal.telefone_exibido ? ` · ${canal.telefone_exibido}` : ''}`
              : 'O WhatsApp ainda não está conectado'}
          </strong>
          <p>
            {canal?.estado === 'ATIVO'
              ? 'A régua abaixo vale para os agendamentos desta clínica.'
              : 'A conta do WhatsApp é da clínica, não nossa — ela precisa ser ligada '
                + 'à nossa parceria na Meta antes de qualquer mensagem sair. '
                + 'Você pode montar a régua agora: ela passa a valer no dia da conexão.'}
          </p>
        </div>
      </div>

      <Erro mensagem={erro} />

      {carregando ? <Carregando /> : (
        <Secao
          titulo="A régua"
          descricao="Quando o sistema fala com o paciente, em relação à consulta dele."
          {...(teto > 0
            ? { resumo: { valor: String(teto), ajuda: 'teto de mensagens neste mês' } }
            : {})}
          acao={podeEditar && (
            <div className="rg-acoes">
              {itens.length > 0 && (
                <button className="botao botao--fantasma" onClick={verFila}>
                  Ver o que sairia agora
                </button>
              )}
              <button className="botao botao--fantasma" onClick={() => setNova(true)}>
                Nova régua
              </button>
            </div>
          )}
        >
          {itens.length === 0 ? (
            <Vazio
              mensagem="Nenhuma régua configurada."
              sugestao="Sem régua, o sistema não fala com o paciente — a confirmação continua sendo no telefone, uma ligação por vez."
              acao={podeEditar && (
                <button className="botao" onClick={aplicarPadrao}>
                  Usar a régua recomendada
                </button>
              )}
            />
          ) : (
            <LinhaDoTempo antes={antes} depois={depois} podeEditar={podeEditar}
                          aoEditar={setEditando} aoAlternar={alternar} />
          )}
        </Secao>
      )}

      {nova && (
        <FormRegua templates={templates} aoFechar={() => setNova(false)}
                   aoSalvar={async () => { setNova(false); await carregar() }} />
      )}

      {editando && (
        <FormRegua regua={editando} templates={templates}
                   aoFechar={() => setEditando(null)}
                   aoSalvar={async () => { setEditando(null); await carregar() }} />
      )}

      {fila && <VerFila itens={fila} aoFechar={() => setFila(null)} />}
    </>
  )
}

/* ══ a linha do tempo ══════════════════════════════════════════════ */

function LinhaDoTempo({ antes, depois, podeEditar, aoEditar, aoAlternar }: {
  antes: Regua[]; depois: Regua[]; podeEditar: boolean
  aoEditar: (r: Regua) => void
  aoAlternar: (r: Regua) => void
}) {
  return (
    <div className="rg-linha">
      <div className="rg-lado">
        <span className="rg-lado__rot">antes da consulta</span>
        {antes.length === 0
          ? <p className="ajuda rg-lado__vazio">Nada sai antes.</p>
          : antes.map((r) => (
              <Cartao key={r.id} r={r} podeEditar={podeEditar}
                      aoEditar={aoEditar} aoAlternar={aoAlternar} />
            ))}
      </div>

      <div className="rg-marco" aria-hidden="true">
        <span className="rg-marco__ponto" />
        <span className="rg-marco__rot">a consulta</span>
      </div>

      <div className="rg-lado">
        <span className="rg-lado__rot">depois da consulta</span>
        {depois.length === 0
          ? <p className="ajuda rg-lado__vazio">Nada sai depois.</p>
          : depois.map((r) => (
              <Cartao key={r.id} r={r} podeEditar={podeEditar}
                      aoEditar={aoEditar} aoAlternar={aoAlternar} />
            ))}
      </div>
    </div>
  )
}

function Cartao({ r, podeEditar, aoEditar, aoAlternar }: {
  r: Regua; podeEditar: boolean
  aoEditar: (r: Regua) => void
  aoAlternar: (r: Regua) => void
}) {
  return (
    <article className={`rg-cartao ${r.ativo ? '' : 'is-desligada'}`}>
      <header>
        <div>
          <strong>{NOME[r.tipo]}</strong>
          <span className="rg-quando">{quando(r.horas_antes)}</span>
        </div>
        {podeEditar && (
          <label className="rg-chave">
            <input type="checkbox" checked={r.ativo}
                   onChange={() => aoAlternar(r)} />
            <span>{r.ativo ? 'ligada' : 'desligada'}</span>
          </label>
        )}
      </header>

      <p className="ajuda">{EXPLICA[r.tipo]}</p>

      <ul className="rg-regras">
        {r.so_primeira && <li>só na primeira vez do paciente</li>}
        {r.min_dias_entre > 0 && (
          <li>no máximo uma a cada {r.min_dias_entre} dias</li>
        )}
        <li>entre {r.nao_antes} e {r.nao_depois}</li>
        {r.template_nome && <li className="mono">{r.template_nome}</li>}
      </ul>

      {/* A régua ligada que não manda nada é a falha mais cara desta
          tela, porque não dá sinal nenhum. O aviso é vermelho e fica
          no cartão, não numa lista de pendências em outro lugar. */}
      {r.ativo && r.muda && (
        <p className="rg-muda">
          {r.muda === 'SEM_TEMPLATE'
            ? 'Esta régua não manda nada: falta escolher o texto aprovado.'
            : `Esta régua não manda nada: o texto está ${
                r.template_estado === 'PENDENTE' ? 'em análise na Meta'
                : r.template_estado === 'REJEITADO' ? 'reprovado pela Meta'
                : 'indisponível'}.`}
        </p>
      )}

      {podeEditar && (
        <button className="rg-editar" onClick={() => aoEditar(r)}>Ajustar</button>
      )}
    </article>
  )
}

/* ══ o formulário ══════════════════════════════════════════════════ */

const TIPOS: Array<Regua['tipo']> = [
  'CONFIRMACAO', 'LEMBRETE', 'POS_ATENDIMENTO', 'RECALL', 'COBRANCA',
]

function FormRegua({ regua, templates, aoFechar, aoSalvar }: {
  regua?: Regua; templates: Template[]
  aoFechar: () => void; aoSalvar: () => Promise<void>
}) {
  const novo = !regua
  const [tipo, setTipo] = useState<Regua['tipo']>(regua?.tipo ?? 'LEMBRETE')
  // Guardado em horas no banco; editado em horas ou dias na tela,
  // porque ninguém pensa "72 horas antes", pensa "3 dias antes".
  const [valor, setValor] = useState(() => {
    const h = Math.abs(regua?.horas_antes ?? 3)
    return h >= 24 && h % 24 === 0 ? h / 24 : h
  })
  const [unidade, setUnidade] = useState<'h' | 'd'>(() => {
    const h = Math.abs(regua?.horas_antes ?? 3)
    return h >= 24 && h % 24 === 0 ? 'd' : 'h'
  })
  const [templateId, setTemplateId] = useState(regua?.template_id ?? '')
  const [soPrimeira, setSoPrimeira] = useState(regua?.so_primeira ?? false)
  const [minDias, setMinDias] = useState(regua?.min_dias_entre ?? 0)
  const [naoAntes, setNaoAntes] = useState(regua?.nao_antes ?? '08:00')
  const [naoDepois, setNaoDepois] = useState(regua?.nao_depois ?? '20:00')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const depoisDaConsulta = tipo === 'POS_ATENDIMENTO'
  const horas = (unidade === 'd' ? valor * 24 : valor) * (depoisDaConsulta ? -1 : 1)

  async function salvar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null); setSalvando(true)
    try {
      const corpo = {
        horas_antes: horas,
        template_id: templateId || null,
        so_primeira: soPrimeira,
        min_dias_entre: minDias,
        nao_antes: naoAntes,
        nao_depois: naoDepois,
      }
      if (novo) await api('/v1/regua', { metodo: 'POST', corpo: { tipo, ...corpo } })
      else await api(`/v1/regua/${regua!.id}`, { metodo: 'PATCH', corpo })
      await aoSalvar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível salvar.')
      setSalvando(false)
    }
  }

  async function apagar() {
    if (!regua) return
    setSalvando(true)
    try {
      await api(`/v1/regua/${regua.id}`, { metodo: 'DELETE' })
      await aoSalvar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível apagar.')
      setSalvando(false)
    }
  }

  const aprovados = templates.filter((t) => t.estado === 'APROVADO')

  return (
    <Folha titulo={novo ? 'Nova régua' : `Ajustar ${NOME[tipo].toLowerCase()}`}
           descricao={EXPLICA[tipo]}
           aoFechar={aoFechar}
           acoes={
             <>
               {!novo && (
                 <button type="button" className="botao botao--perigo"
                         onClick={apagar} disabled={salvando}>
                   Apagar
                 </button>
               )}
               <button type="submit" form="form-regua" className="botao"
                       disabled={salvando || valor < 1}>
                 {salvando ? 'Salvando…' : 'Salvar'}
               </button>
             </>
           }>
      <form id="form-regua" onSubmit={salvar} className="rg-form">
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        {novo && (
          <div className="campo">
            <label htmlFor="rg-tipo">O que é</label>
            <select id="rg-tipo" className="entrada" value={tipo}
                    onChange={(e) => setTipo(e.target.value as Regua['tipo'])}>
              {TIPOS.map((t) => <option key={t} value={t}>{NOME[t]}</option>)}
            </select>
          </div>
        )}

        <fieldset className="rg-momento">
          <legend>Quando sai</legend>
          <div className="rg-momento__campos">
            <input className="entrada mono" type="number" min={1} max={365}
                   value={valor} aria-label="quantidade"
                   onChange={(e) => setValor(Number(e.target.value))} />
            <select className="entrada" value={unidade} aria-label="unidade"
                    onChange={(e) => setUnidade(e.target.value as 'h' | 'd')}>
              <option value="h">horas</option>
              <option value="d">dias</option>
            </select>
            <strong className="rg-momento__lado">
              {depoisDaConsulta ? 'depois da consulta' : 'antes da consulta'}
            </strong>
          </div>
          <p className="ajuda">
            {depoisDaConsulta
              ? 'O tipo escolhido acontece depois do atendimento, e só alcança quem foi atendido.'
              : 'Alcança quem ainda vai vir. Quem cancelar sai da fila na hora.'}
          </p>
        </fieldset>

        <div className="campo">
          <label htmlFor="rg-tmpl">Texto aprovado</label>
          <select id="rg-tmpl" className="entrada" value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">— nenhum —</option>
            {aprovados.map((t) => (
              <option key={t.id} value={t.id}>{t.nome}</option>
            ))}
          </select>
          {/* Sem template aprovado a régua fica muda fora da janela de
              24h, que é a situação normal. Dizer isso aqui é mais
              barato que o cliente descobrir pela ausência. */}
          <span className="ajuda">
            {aprovados.length === 0
              ? 'Nenhum texto aprovado ainda. Fora de uma conversa já aberta pelo paciente, '
                + 'a Meta só entrega texto que ela aprovou — sem isso, esta régua não manda nada.'
              : 'Fora de uma conversa aberta pelo paciente, só sai texto aprovado pela Meta.'}
          </span>
        </div>

        <fieldset className="rg-limites">
          <legend>Para não virar incômodo</legend>

          <label className="rg-check">
            <input type="checkbox" checked={soPrimeira}
                   onChange={(e) => setSoPrimeira(e.target.checked)} />
            <span>
              <strong>Só na primeira vez do paciente</strong>
              <em>Quem faz série de sessões não precisa disso toda semana.</em>
            </span>
          </label>

          <div className="campo">
            <label htmlFor="rg-min">Silêncio entre mensagens deste tipo</label>
            <div className="rg-min">
              <input id="rg-min" className="entrada mono" type="number"
                     min={0} max={90} value={minDias}
                     onChange={(e) => setMinDias(Number(e.target.value))} />
              <span>dias</span>
            </div>
            <span className="ajuda">
              {minDias === 0
                ? 'Sem silêncio: um paciente com três sessões na semana recebe três mensagens.'
                : `No máximo uma a cada ${minDias} ${minDias === 1 ? 'dia' : 'dias'} por paciente.`}
            </span>
          </div>

          <div className="rg-faixa">
            <div className="campo">
              <label htmlFor="rg-de">Não antes de</label>
              <input id="rg-de" className="entrada mono" type="time"
                     value={naoAntes} onChange={(e) => setNaoAntes(e.target.value)} />
            </div>
            <div className="campo">
              <label htmlFor="rg-ate">Não depois de</label>
              <input id="rg-ate" className="entrada mono" type="time"
                     value={naoDepois} onChange={(e) => setNaoDepois(e.target.value)} />
            </div>
          </div>
          <span className="ajuda">
            Mensagem fora de hora vira bloqueio, e o bloqueio derruba a entrega
            para todos os pacientes desta clínica — não só para quem reclamou.
          </span>
        </fieldset>
      </form>
    </Folha>
  )
}

/* ══ o que sairia agora ════════════════════════════════════════════ */

/**
 * A prova. Antes de existir despachante, isto é o que mostra que a
 * régua faz o que a clínica acha que faz — e é a tela que se usa para
 * revisar uma mudança antes de ligá-la de verdade.
 */
function VerFila({ itens, aoFechar }: { itens: ItemFila[]; aoFechar: () => void }) {
  return (
    <Folha titulo="O que sairia agora"
           descricao="A régua aplicada à agenda de verdade, neste instante. Nada é enviado."
           aoFechar={aoFechar}
           acoes={
             <button type="button" className="botao" onClick={aoFechar}>Fechar</button>
           }>
      {itens.length === 0 ? (
        <Vazio
          mensagem="Nada a enviar neste momento."
          sugestao="É o esperado fora dos horários em que a régua dispara. Volte perto do momento configurado."
        />
      ) : (
        <table className="tabela rg-fila">
          <thead>
            <tr>
              <th>Paciente</th><th>O quê</th>
              <th>Consulta</th><th>Sairia</th><th>Como</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((i) => (
              <tr key={`${i.regua_id}-${i.agendamento_id}`}>
                <td>{i.paciente}</td>
                <td>{NOME[i.tipo as Regua['tipo']] ?? i.tipo}</td>
                <td className="mono">{hora(i.inicio)}</td>
                <td className="mono">{hora(i.previsto_para)}</td>
                <td>
                  {i.janela_aberta
                    ? <span className="selo selo--ok">conversa aberta</span>
                    : <span className="selo selo--neutro">{i.template_nome}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Folha>
  )
}
